import { useEffect, useRef, useState } from "react";
import ContainerVisual from "./formatted/LoadImages";
import { formattedDate, formattedTime } from "./formatted/FormattedDateAndTime";
import { useTranslation } from "react-i18next";
import { CropImage } from "./utils/ImageTreatment";
import { capitalizeFirstLetter } from "./utils/Date";
import SortingErrorsWarningIcon from "./icons/SortingErrorsWarningIcon";

// Facteur d'agrandissement des vignettes (légèrement plus larges, format carré).
const ANOMALY_THUMBNAIL_SCALE = 2.05;
// Marge blanche autour de l'image (px).
const ANOMALY_IMAGE_MARGIN = 16;
// Marge horizontale entre deux vignettes (px, appliquée de chaque côté).
const ANOMALY_ITEM_SPACING = 8;
// Hauteur du pied de carte (titre + métadonnées).
const ANOMALY_FOOTER_HEIGHT = 68;
// Nombre de vignettes affichées par page par défaut (pagination par points).
const ANOMALY_ITEMS_PER_PAGE = 6;

const ImageListAnomalies = ({
  container,
  dataSortedByAnomalies,
  findHistoryId,
  imageWidth,
  imageHeight,
  historyCycle,
  selectedReadingTime = null,
}) => {
  const { t } = useTranslation();
  const [activePage, setActivePage] = useState(0);
  const scrollContainerRef = useRef(null);
  const scrollSettleTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(scrollSettleTimeoutRef.current), []);

  if (!dataSortedByAnomalies || dataSortedByAnomalies.length === 0) {
    return null;
  }

  const baseSize = Math.max(imageWidth || 140, imageHeight || 140);
  const cardSize = Math.round(baseSize * ANOMALY_THUMBNAIL_SCALE);
  const imageAreaSize = cardSize - ANOMALY_FOOTER_HEIGHT;
  const displaySize = Math.max(0, imageAreaSize - ANOMALY_IMAGE_MARGIN * 2);
  const itemOuterWidth = cardSize + ANOMALY_ITEM_SPACING * 2;
  const pageWidth = itemOuterWidth * ANOMALY_ITEMS_PER_PAGE;
  const totalPages = Math.ceil(
    dataSortedByAnomalies.length / ANOMALY_ITEMS_PER_PAGE,
  );

  const scrollToPage = (pageIndex) => {
    setActivePage(pageIndex);
    scrollContainerRef.current?.scrollTo({
      left: pageIndex * pageWidth,
      behavior: "smooth",
    });
  };

  // On attend que le scroll (smooth ou manuel) soit terminé avant de recalculer
  // la page active : lire scrollLeft pendant l'animation donne des valeurs
  // intermédiaires qui font "revenir en arrière" le point actif.
  const handleScroll = (e) => {
    const { scrollLeft } = e.target;
    clearTimeout(scrollSettleTimeoutRef.current);
    scrollSettleTimeoutRef.current = setTimeout(() => {
      const newPage = Math.round(scrollLeft / pageWidth);
      setActivePage((current) => (current === newPage ? current : newPage));
    }, 120);
  };

  return (
    <section className="flex flex-col w-full mb-3">
      <div className="char-section-header char-section-header--general-info flex flex-row flex-wrap gap-3 !justify-start items-center !mb-6">
        <h2 className="char-section-header-title !mb-0">
          {t("quality.mostSortingErrorsTitle")}
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium bg-[#FBEBED] text-[#AF2F3C] whitespace-nowrap">
          <SortingErrorsWarningIcon />
          {t("quality.mostSortingErrorsCount", {
            count: dataSortedByAnomalies.length,
            total: historyCycle.length,
          })}
        </span>
      </div>

      <div
        className="flex overflow-x-auto w-full"
        style={{ overscrollBehaviorX: "contain" }}
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <div className="flex">
          {dataSortedByAnomalies.map((item, index) => {
            const isSelected = selectedReadingTime === item._time;

            return (
              <div
                key={index}
                className="flex-none"
                style={{
                  marginRight: ANOMALY_ITEM_SPACING * 2,
                }}
              >
                <div
                  className={`flex flex-col cursor-pointer rounded-xl overflow-hidden transition-shadow box-border ${
                    isSelected
                      ? "border-[3px] border-[#00A7A6]"
                      : "border border-gray-300"
                  }`}
                  style={{ width: cardSize, height: cardSize }}
                  onClick={() => {
                    findHistoryId(item._time);
                  }}
                >
                  <div
                    className="bg-white border-b border-gray-200 flex items-center justify-center shrink-0"
                    style={{
                      height: imageAreaSize,
                      padding: ANOMALY_IMAGE_MARGIN,
                    }}
                  >
                    {item.image_url ? (
                      <CropImage
                        container={container}
                        data={item}
                        imgDivWidth={displaySize}
                        imgDivHeight={displaySize}
                        isTooltip={true}
                        isMap={false}
                        isCharacterization={false}
                        displayLegend={false}
                        materialsDropdownToDisplay={[]}
                        showCheckbox={false}
                        displayToBlock={false}
                      />
                    ) : (
                      <ContainerVisual
                        type={item.type}
                        width={displaySize}
                        height={displaySize}
                      />
                    )}
                  </div>

                  <div
                    className="flex flex-col w-full items-start text-left bg-white px-3 py-2 gap-0.5 justify-center"
                    style={{ height: ANOMALY_FOOTER_HEIGHT }}
                  >
                    <span className="text-sm font-semibold text-[#222222] leading-tight">
                      {`${capitalizeFirstLetter(t("record"))} nº ${[...historyCycle].reverse().findIndex((entry) => entry._time === item._time) + 1}`}
                    </span>
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                        {item.anomalies}{" "}
                        {item.anomalies > 1
                          ? capitalizeFirstLetter(t("sorting_errors"))
                          : capitalizeFirstLetter(t("sorting_error"))}
                      </span>
                      <span
                        className="text-gray-400 leading-none"
                        style={{ fontSize: "16px" }}
                        aria-hidden
                      >
                        •
                      </span>
                      <span className="text-xs text-gray-400 truncate">
                        {`${formattedDate(item._time, t)} ${t("à")} ${formattedTime(item._time)}`}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          {Array.from({ length: totalPages }).map((_, pageIndex) => (
            <button
              key={pageIndex}
              type="button"
              aria-label={`Page ${pageIndex + 1}`}
              onClick={() => scrollToPage(pageIndex)}
              className={`rounded-full transition-all ${
                pageIndex === activePage
                  ? "w-2.5 h-2.5 bg-cyan"
                  : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default ImageListAnomalies;
